import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  Building2, FileText, Calculator, Shield, CheckCircle, 
  Briefcase, MapPin, ClipboardCheck, DollarSign, TrendingUp,
  Phone, Mail, ArrowRight, Sparkles, Star, Users, Clock,
  Target, Lightbulb, Award, Calendar, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BusinessStudio = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: '',
    message: ''
  });

  const services = [
    {
      icon: Building2,
      title: t('businessStudio.service1Title'),
      titleJp: t('businessStudio.service1TitleJp'),
      description: t('businessStudio.service1Desc'),
      color: '#00ffff'
    },
    {
      icon: ClipboardCheck,
      title: t('businessStudio.service2Title'),
      titleJp: t('businessStudio.service2TitleJp'),
      description: t('businessStudio.service2Desc'),
      color: '#ff00ff'
    },
    {
      icon: FileText,
      title: t('businessStudio.service3Title'),
      titleJp: t('businessStudio.service3TitleJp'),
      description: t('businessStudio.service3Desc'),
      color: '#00ff88'
    },
    {
      icon: Shield,
      title: t('businessStudio.service4Title'),
      titleJp: t('businessStudio.service4TitleJp'),
      description: t('businessStudio.service4Desc'),
      color: '#ffff00'
    }
  ];

  const taxBenefits = [
    { icon: Calculator, text: t('businessStudio.taxBenefit1') },
    { icon: FileText, text: t('businessStudio.taxBenefit2') },
    { icon: TrendingUp, text: t('businessStudio.taxBenefit3') },
    { icon: DollarSign, text: t('businessStudio.taxBenefit4') },
    { icon: Clock, text: t('businessStudio.taxBenefit5') },
    { icon: Award, text: t('businessStudio.taxBenefit6') }
  ];

  const whyChooseUs = [
    { icon: MapPin, title: t('businessStudio.why1Title'), desc: t('businessStudio.why1Desc') },
    { icon: Users, title: t('businessStudio.why2Title'), desc: t('businessStudio.why2Desc') },
    { icon: Target, title: t('businessStudio.why3Title'), desc: t('businessStudio.why3Desc') },
    { icon: Lightbulb, title: t('businessStudio.why4Title'), desc: t('businessStudio.why4Desc') }
  ];

  // Submit form as a quote request
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const quoteData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        sign_type: `Business Studio - ${formData.businessType}`,
        message: formData.message,
        source: 'Business Studio Consultation'
      };

      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', businessType: '', message: '' });
        setTimeout(() => {
          setShowContactForm(false);
          setSubmitSuccess(false);
        }, 3000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form. Please try again.');
    }

    setIsSubmitting(false);
  };

  return (
    <div 
      className="min-h-screen transition-colors duration-300"
      style={{ 
        backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
        color: isDark ? '#ffffff' : '#0f172a'
      }}
    >
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 px-4 overflow-hidden"
        style={{
          background: isDark 
            ? 'linear-gradient(135deg, #0a0a0a 0%, #1a0a2e 50%, #0a0a0a 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #f8fafc 100%)'
        }}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-20 left-10 w-64 h-64 rounded-full blur-3xl opacity-30 animate-pulse"
            style={{ backgroundColor: '#00ffff' }}
          />
          <div 
            className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl opacity-20 animate-pulse"
            style={{ backgroundColor: '#ff00ff', animationDelay: '1s' }}
          />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Briefcase 
                className="w-12 h-12"
                style={{ 
                  color: isDark ? '#00ffff' : '#0ea5e9',
                  filter: isDark ? 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.5))' : 'none'
                }}
              />
              <Sparkles 
                className="w-8 h-8 animate-pulse"
                style={{ color: isDark ? '#ffff00' : '#eab308' }}
              />
            </div>
            
            <h1 
              className="text-4xl md:text-6xl font-bold mb-4"
              style={{
                color: isDark ? '#00ffff' : '#0ea5e9',
                textShadow: isDark 
                  ? '0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #ff00ff, 0 0 40px #ff00ff'
                  : 'none'
              }}
            >
              {t('businessStudio.title')}
            </h1>
            
            <p 
              className="text-xl md:text-2xl mb-2"
              style={{ color: isDark ? '#00ff88' : '#10b981' }}
            >
              {t('businessStudio.titleJp')}
            </p>
            
            <p 
              className="text-lg md:text-xl max-w-3xl mx-auto"
              style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
            >
              {t('businessStudio.subtitle')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => setShowContactForm(true)}
              className="px-8 py-4 text-lg font-semibold rounded-full"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                  : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                color: isDark ? '#000' : '#fff',
                boxShadow: isDark ? '0 0 30px rgba(0, 255, 255, 0.4)' : '0 10px 40px rgba(14, 165, 233, 0.3)'
              }}
            >
              {t('businessStudio.getStarted')}
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </Button>
            <Button
              onClick={() => setShowContactForm(true)}
              variant="outline"
              className="px-8 py-4 text-lg font-semibold rounded-full"
              style={{
                borderColor: isDark ? '#ff00ff' : '#8b5cf6',
                color: isDark ? '#ff00ff' : '#8b5cf6'
              }}
            >
              {t('businessStudio.freeConsultation')}
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: isDark ? '#ffffff' : '#0f172a' }}
            >
              {t('businessStudio.servicesTitle')}
            </h2>
            <p 
              className="text-lg"
              style={{ color: isDark ? '#888' : '#64748b' }}
            >
              {t('businessStudio.servicesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                  border: `2px solid ${isDark ? service.color + '40' : '#e2e8f0'}`,
                  boxShadow: isDark 
                    ? `0 0 30px ${service.color}20`
                    : '0 10px 40px rgba(0, 0, 0, 0.05)'
                }}
              >
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{ 
                    backgroundColor: isDark ? service.color + '20' : service.color + '10',
                    boxShadow: isDark ? `0 0 20px ${service.color}40` : 'none'
                  }}
                >
                  <service.icon 
                    className="w-8 h-8"
                    style={{ color: service.color }}
                  />
                </div>
                <h3 
                  className="text-xl font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {service.title}
                </h3>
                <p 
                  className="text-sm mb-3"
                  style={{ color: service.color }}
                >
                  {service.titleJp}
                </p>
                <p style={{ color: isDark ? '#a0a0a0' : '#64748b' }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Management Section - FEATURED */}
      <section 
        className="py-20 px-4 relative overflow-hidden"
        style={{
          background: isDark 
            ? 'linear-gradient(180deg, #0a0a0a 0%, #1a0a2e 50%, #0a0a0a 100%)'
            : 'linear-gradient(180deg, #f8fafc 0%, #dbeafe 50%, #f8fafc 100%)'
        }}
      >
        <div className="absolute top-0 left-0 w-full h-2" style={{ background: 'linear-gradient(90deg, #00ffff, #ff00ff, #ffff00)' }} />
        
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                backgroundColor: isDark ? 'rgba(0, 255, 136, 0.2)' : 'rgba(16, 185, 129, 0.1)',
                border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`
              }}
            >
              <Star className="w-5 h-5" style={{ color: isDark ? '#ffff00' : '#eab308' }} />
              <span style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                {t('businessStudio.featuredService')}
              </span>
            </div>
            
            <h2 
              className="text-3xl md:text-5xl font-bold mb-4"
              style={{
                color: isDark ? '#00ff88' : '#10b981',
                textShadow: isDark 
                  ? '0 0 10px #00ff88, 0 0 20px #00ff88, 0 0 30px #00ffff'
                  : 'none'
              }}
            >
              {t('businessStudio.taxTitle')}
            </h2>
            <p 
              className="text-xl mb-2"
              style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}
            >
              {t('businessStudio.taxTitleJp')}
            </p>
            <p 
              className="text-lg max-w-3xl mx-auto"
              style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
            >
              {t('businessStudio.taxSubtitle')}
            </p>
          </div>

          <div 
            className="p-8 md:p-12 rounded-3xl mb-12"
            style={{
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#ffffff',
              border: `3px solid ${isDark ? '#00ff88' : '#10b981'}`,
              boxShadow: isDark 
                ? '0 0 60px rgba(0, 255, 136, 0.2), inset 0 0 60px rgba(0, 255, 136, 0.05)'
                : '0 20px 60px rgba(16, 185, 129, 0.15)'
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {taxBenefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-xl"
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.05)'
                  }}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ 
                      backgroundColor: isDark ? '#00ff88' : '#10b981',
                      boxShadow: isDark ? '0 0 20px rgba(0, 255, 136, 0.5)' : 'none'
                    }}
                  >
                    <benefit.icon className="w-6 h-6" style={{ color: isDark ? '#000' : '#fff' }} />
                  </div>
                  <span 
                    className="font-medium"
                    style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                  >
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <div 
                className="inline-block p-6 rounded-2xl"
                style={{
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 255, 255, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)'
                }}
              >
                <p 
                  className="text-2xl font-bold mb-2"
                  style={{ color: isDark ? '#00ff88' : '#10b981' }}
                >
                  {t('businessStudio.taxPricing')}
                </p>
                <p style={{ color: isDark ? '#888' : '#64748b' }}>
                  {t('businessStudio.taxPricingJp')}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => {
                setFormData({ ...formData, businessType: 'tax' });
                setShowContactForm(true);
              }}
              className="px-10 py-5 text-xl font-bold rounded-full"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #00ff88 0%, #00ffff 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)',
                color: isDark ? '#000' : '#fff',
                boxShadow: isDark 
                  ? '0 0 40px rgba(0, 255, 136, 0.5)'
                  : '0 15px 50px rgba(16, 185, 129, 0.4)'
              }}
            >
              <Calculator className="w-6 h-6 mr-2 inline" />
              {t('businessStudio.startTaxManagement')}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: isDark ? '#ffffff' : '#0f172a' }}
            >
              {t('businessStudio.whyChooseTitle')}
            </h2>
            <p style={{ color: isDark ? '#888' : '#64748b' }}>
              {t('businessStudio.whyChooseTitleJp')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                }}
              >
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ 
                    backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(139, 92, 246, 0.1)',
                    boxShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.3)' : 'none'
                  }}
                >
                  <item.icon 
                    className="w-8 h-8"
                    style={{ color: isDark ? '#ff00ff' : '#8b5cf6' }}
                  />
                </div>
                <h3 
                  className="font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {item.title}
                </h3>
                <p style={{ color: isDark ? '#888' : '#64748b' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowContactForm(false)}
          />
          <div 
            className="relative w-full max-w-lg rounded-3xl p-8"
            style={{
              backgroundColor: isDark ? '#111111' : '#ffffff',
              border: `2px solid ${isDark ? '#00ffff' : '#0ea5e9'}`,
              boxShadow: isDark 
                ? '0 0 60px rgba(0, 255, 255, 0.3)'
                : '0 20px 60px rgba(0, 0, 0, 0.2)'
            }}
          >
            {submitSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                <p className="text-xl font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                  {t('businessStudio.formSuccess')}
                </p>
              </div>
            ) : (
              <>
                <h3 
                  className="text-2xl font-bold mb-6 text-center"
                  style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}
                >
                  {t('businessStudio.formTitle')}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder={t('businessStudio.formName')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full p-4 rounded-xl"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                      color: isDark ? '#fff' : '#000'
                    }}
                  />
                  <input
                    type="email"
                    placeholder={t('businessStudio.formEmail')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full p-4 rounded-xl"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                      color: isDark ? '#fff' : '#000'
                    }}
                  />
                  <input
                    type="tel"
                    placeholder={t('businessStudio.formPhone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full p-4 rounded-xl"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                      color: isDark ? '#fff' : '#000'
                    }}
                  />
                  <select
                    value={formData.businessType}
                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                    required
                    className="w-full p-4 rounded-xl"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                      color: isDark ? '#fff' : '#000'
                    }}
                  >
                    <option value="">{t('businessStudio.formService')}</option>
                    <option value="space">{t('businessStudio.formServiceSpace')}</option>
                    <option value="permits">{t('businessStudio.formServicePermits')}</option>
                    <option value="registration">{t('businessStudio.formServiceRegistration')}</option>
                    <option value="tax">{t('businessStudio.formServiceTax')}</option>
                    <option value="all">{t('businessStudio.formServiceAll')}</option>
                  </select>
                  <textarea
                    placeholder={t('businessStudio.formMessage')}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="w-full p-4 rounded-xl resize-none"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                      color: isDark ? '#fff' : '#000'
                    }}
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 text-lg font-bold rounded-xl"
                    style={{
                      background: isDark 
                        ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                        : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                      color: isDark ? '#000' : '#fff'
                    }}
                  >
                    {isSubmitting ? t('common.sending') : t('businessStudio.formSubmit')}
                  </Button>
                </form>
              </>
            )}
            
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
              style={{ color: isDark ? '#666' : '#94a3b8' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BusinessStudio;
